import React, { useState } from 'react';
import { TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { ServiceIcon, formatDateStr } from './calendarHelpers';

interface WeeklyPlannerModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedWeek: number;
  selectedYear: number;
  isSaving: boolean;
  isEditor: boolean;
  weeklyVolumes: Record<string, { L1: number; L3: number }>;
  setWeeklyVolumes: React.Dispatch<React.SetStateAction<Record<string, { L1: number; L3: number }>>>;
  onSaveWeeklyVolumes: () => Promise<void>;
  weeklyCutoffs: any[];
  onSaveCutoff: (payload: {
    date: string;
    lineId: 'L1' | 'L3' | 'Elaboración';
    serviceType: 'electricity' | 'water' | 'steam' | 'co2';
    plannedStartTime: string;
    plannedEndTime: string;
    cutoffReason: string;
  }) => Promise<void>;
  onDeleteCutoff: (id: string) => Promise<void>;
  daysOfWeekList: Date[];
}

export const WeeklyPlannerModal: React.FC<WeeklyPlannerModalProps> = ({
  isOpen,
  onClose,
  selectedWeek,
  selectedYear,
  isSaving,
  isEditor,
  weeklyVolumes,
  setWeeklyVolumes,
  onSaveWeeklyVolumes,
  weeklyCutoffs,
  onSaveCutoff,
  onDeleteCutoff,
  daysOfWeekList
}) => {
  const [plannerDay, setPlannerDay] = useState<string>(() => {
    return daysOfWeekList.length > 0 ? formatDateStr(daysOfWeekList[0]) : '';
  });
  const [lineId, setLineId] = useState<'L1' | 'L3' | 'Elaboración'>('L1');
  const [serviceType, setServiceType] = useState<'electricity' | 'water' | 'steam' | 'co2'>('electricity');
  const [plannedStartTime, setPlannedStartTime] = useState('13:00');
  const [plannedEndTime, setPlannedEndTime] = useState('17:00');
  const [cutoffReason, setCutoffReason] = useState('CIP');

  if (!isOpen) return null;

  const handleVolumeChange = (dateStr: string, line: 'L1' | 'L3', value: number) => {
    setWeeklyVolumes(prev => ({
      ...prev,
      [dateStr]: {
        ...prev[dateStr],
        [line]: value
      }
    }));
  };

  const handleAddCutoff = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!plannerDay) return;

    await onSaveCutoff({
      date: plannerDay,
      lineId,
      serviceType,
      plannedStartTime,
      plannedEndTime,
      cutoffReason
    });

    // Reset values
    setPlannedStartTime('13:00');
    setPlannedEndTime('17:00');
    setCutoffReason('CIP');
  };

  const daysNames = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-5xl w-full max-h-[92vh] overflow-y-auto border border-gray-100 animate-in zoom-in-95 duration-200 flex flex-col">
        
        {/* Cabecera del Modal */}
        <div className="p-6 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white z-10 flex-shrink-0">
          <div>
            <span className="text-[10px] font-black uppercase tracking-widest text-[#d1a228] bg-amber-50 px-2.5 py-1 rounded-full">
              Planificación Semanal Masiva
            </span>
            <h3 className="text-xl font-black text-gray-900 mt-2">
              Calendario de Operación y Cortes - Semana {selectedWeek} ({selectedYear})
            </h3>
          </div>
          <button 
            onClick={onClose} 
            disabled={isSaving} 
            className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-xl transition-all"
          >
            ✕
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1 grid grid-cols-1 lg:grid-cols-12 gap-8">
          
          {/* SECCIÓN IZQUIERDA: VOLÚMENES PROYECTADOS (COL-SPAN 5) */}
          <div className="lg:col-span-5 space-y-6">
            <div className="flex justify-between items-center border-b border-slate-100 pb-2">
              <h4 className="text-xs font-black text-slate-800 uppercase tracking-wider">
                Volúmenes Proyectados (hl)
              </h4>
            </div>

            <div className="overflow-x-auto border border-slate-200 rounded-2xl shadow-sm bg-slate-50/50">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-slate-100 text-slate-700 border-b border-slate-200">
                    <th className="p-3 font-extrabold uppercase text-[9px]">Día</th>
                    <th className="p-3 font-extrabold uppercase text-[9px] text-right">L1 (hl)</th>
                    <th className="p-3 font-extrabold uppercase text-[9px] text-right">L3 (hl)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-150 bg-white">
                  {daysOfWeekList.map(day => {
                    const dateStr = formatDateStr(day);
                    const volumes = weeklyVolumes[dateStr] || { L1: 0, L3: 0 };
                    
                    return (
                      <tr key={`weekly-vol-row-${dateStr}`} className="hover:bg-slate-50/50 transition-colors">
                        <td className="p-3 font-bold text-slate-800">
                          {daysNames[day.getDay()]} {day.getDate()}
                        </td>
                        <td className="p-2 text-right">
                          <input
                            type="number"
                            disabled={!isEditor || isSaving}
                            value={volumes.L1 || ''}
                            onChange={(e) => handleVolumeChange(dateStr, 'L1', Number(e.target.value))}
                            className="w-20 text-right p-1.5 bg-white border border-slate-200 rounded-lg text-xs font-bold focus:ring-1 focus:ring-[#E2B53F]"
                            placeholder="0"
                          />
                        </td>
                        <td className="p-2 text-right">
                          <input
                            type="number"
                            disabled={!isEditor || isSaving}
                            value={volumes.L3 || ''}
                            onChange={(e) => handleVolumeChange(dateStr, 'L3', Number(e.target.value))}
                            className="w-20 text-right p-1.5 bg-white border border-slate-200 rounded-lg text-xs font-bold focus:ring-1 focus:ring-[#E2B53F]"
                            placeholder="0"
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {isEditor && (
              <button
                onClick={onSaveWeeklyVolumes}
                disabled={isSaving}
                className="w-full py-3 bg-zinc-950 hover:bg-zinc-900 text-white rounded-2xl text-xs font-black uppercase tracking-wider transition-all shadow-md hover:shadow-lg"
              >
                {isSaving ? 'Guardando...' : 'Guardar Volúmenes Semanales'}
              </button>
            )}
          </div>

          {/* SECCIÓN DERECHA: REGISTRO Y GESTIÓN DE CORTES (COL-SPAN 7) */}
          <div className="lg:col-span-7 space-y-6">
            <h4 className="text-xs font-black text-slate-800 uppercase tracking-wider border-b border-slate-100 pb-2">
              Programar Cortes de Suministros
            </h4>

            {/* Formulario de creación */}
            {isEditor && (
              <form onSubmit={handleAddCutoff} className="p-4 bg-slate-50 border border-slate-200 rounded-2xl shadow-sm space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[9px] font-bold text-slate-400 uppercase mb-1">Día del Corte</label>
                    <select
                      value={plannerDay}
                      onChange={(e) => setPlannerDay(e.target.value)}
                      className="w-full text-xs font-semibold p-2 bg-white rounded-xl border border-slate-200 focus:ring-1 focus:ring-[#E2B53F]"
                    >
                      {daysOfWeekList.map(d => {
                        const str = formatDateStr(d);
                        return (
                          <option key={`cutoff-day-opt-${str}`} value={str}>
                            {daysNames[d.getDay()]} {d.getDate()}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  <div>
                    <label className="block text-[9px] font-bold text-slate-400 uppercase mb-1">Línea o Área afectada</label>
                    <select
                      value={lineId}
                      onChange={(e) => setLineId(e.target.value as any)}
                      className="w-full text-xs font-semibold p-2 bg-white rounded-xl border border-slate-200 focus:ring-1 focus:ring-[#E2B53F]"
                    >
                      <option value="L1">Línea L1</option>
                      <option value="L3">Línea L3</option>
                      <option value="Elaboración">Elaboración</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-[9px] font-bold text-slate-400 uppercase mb-1">Servicio</label>
                    <select
                      value={serviceType}
                      onChange={(e) => setServiceType(e.target.value as any)}
                      className="w-full text-xs font-semibold p-2 bg-white rounded-xl border border-slate-200 focus:ring-1 focus:ring-[#E2B53F]"
                    >
                      <option value="electricity">Electricidad</option>
                      <option value="water">Agua</option>
                      <option value="steam">Vapor</option>
                      <option value="co2">CO2</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-[9px] font-bold text-slate-400 uppercase mb-1">Hora Inicio</label>
                    <input
                      type="time"
                      value={plannedStartTime}
                      onChange={(e) => setPlannedStartTime(e.target.value)}
                      className="w-full p-2 bg-white border border-slate-200 rounded-xl text-xs focus:ring-1 focus:ring-[#E2B53F]"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-[9px] font-bold text-slate-400 uppercase mb-1">Hora Fin</label>
                    <input
                      type="time"
                      value={plannedEndTime}
                      onChange={(e) => setPlannedEndTime(e.target.value)}
                      className="w-full p-2 bg-white border border-slate-200 rounded-xl text-xs focus:ring-1 focus:ring-[#E2B53F]"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[9px] font-bold text-slate-400 uppercase mb-1">Motivo / Causa del Corte</label>
                  <input
                    type="text"
                    value={cutoffReason}
                    onChange={(e) => setCutoffReason(e.target.value)}
                    className="w-full p-2 bg-white rounded-xl border border-slate-200 text-xs focus:ring-1 focus:ring-[#E2B53F]"
                    placeholder="Ej. CIP Extendido, Mantenimiento preventivo, etc."
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSaving}
                  className="w-full py-2.5 bg-[#E2B53F] hover:bg-[#c99f30] text-gray-900 rounded-xl text-xs font-black uppercase tracking-wider transition-all shadow-sm"
                >
                  {isSaving ? 'Guardando...' : 'Programar Corte de Servicio'}
                </button>
              </form>
            )}

            {/* Listado de cortes de la semana */}
            <div className="space-y-2 max-h-[350px] overflow-y-auto pr-1">
              {weeklyCutoffs.length === 0 ? (
                <div className="text-center py-10 border border-dashed border-slate-200 rounded-2xl bg-slate-50/50">
                  <p className="text-xs text-slate-400 font-bold">No hay cortes programados en esta semana.</p>
                </div>
              ) : (
                weeklyCutoffs.map(cut => {
                  const cutDay = new Date(cut.date + 'T00:00:00');
                  
                  return (
                    <div
                      key={cut.id}
                      className="flex justify-between items-center p-3 border border-slate-200 rounded-xl bg-white shadow-sm hover:shadow-md transition-all"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-1.5 bg-slate-100 rounded-lg text-slate-600">
                          <ServiceIcon type={cut.serviceType} className="h-4 w-4" />
                        </div>
                        <div>
                          <h5 className="text-[11px] font-black text-slate-900 uppercase tracking-wider flex items-center gap-2">
                            <span>
                              {cut.serviceType === 'electricity' ? 'Electricidad' : cut.serviceType === 'water' ? 'Agua' : cut.serviceType === 'steam' ? 'Vapor' : 'CO2'} - {cut.lineId}
                            </span>
                            {cut.cutoffReason && (
                              <span className="bg-amber-50 text-[#d1a228] text-[8px] font-extrabold uppercase px-1.5 py-0.5 rounded-full border border-amber-100">
                                {cut.cutoffReason}
                              </span>
                            )}
                          </h5>
                          <p className="text-[10px] text-slate-500 font-semibold mt-0.5">
                            {daysNames[cutDay.getDay()]} {cutDay.getDate()} |{' '}
                            <span className="font-bold text-slate-800">
                              {cut.plannedStartTime} - {cut.plannedEndTime}
                            </span>
                          </p>
                        </div>
                      </div>

                      {isEditor && (
                        <button
                          onClick={() => onDeleteCutoff(cut.id)}
                          className="p-2 hover:bg-rose-50 text-slate-400 hover:text-rose-600 rounded-xl transition-all"
                          title="Eliminar corte programado"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
